'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { useRouter, usePathname } from 'next/navigation';

interface Category {
  id: number;
  name: string;
  slug: string;
  type: string;
  isActive: boolean;
  sortOrder: number;
  parentId?: number;
  children?: Category[];
}

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [applicationCategories, setApplicationCategories] = useState<Category[]>([]);
  const [applicationParentCategory, setApplicationParentCategory] = useState<Category | null>(null);
  const [newsCategories, setNewsCategories] = useState<Category[]>([]);
  const [newsParentCategory, setNewsParentCategory] = useState<Category | null>(null);
  const [aboutCategories, setAboutCategories] = useState<Category[]>([]);
  const [aboutParentCategory, setAboutParentCategory] = useState<Category | null>(null);
  const [caseCategories, setCaseCategories] = useState<Category[]>([]);
  const [caseParentCategory, setCaseParentCategory] = useState<Category | null>(null);
  const [contactParentCategory, setContactParentCategory] = useState<Category | null>(null);
  const [siteLogo, setSiteLogo] = useState('');
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(true);
  const [systemSettings, setSystemSettings] = useState<any>({});
  const router = useRouter();
  const pathname = usePathname();

  // 获取应用栏目数据及其子栏目
  const fetchApplicationCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const result = await response.json();
      
      if (result.success) {
        // 获取应用范围栏目（ID为2）的信息
        const parentCategory = result.data.find((cat: Category) => cat.id === 2);
        if (parentCategory) {
          setApplicationParentCategory(parentCategory);
        }
        
        // 筛选应用范围栏目（ID为2）下的子栏目，并按sortOrder排序
        const appCategories = result.data
          .filter((cat: Category) => cat.parentId === 2 && cat.isActive)
          .sort((a: Category, b: Category) => a.sortOrder - b.sortOrder);
        
        setApplicationCategories(appCategories);
        
        // 获取新闻中心栏目（ID为1）的信息
        const newsParent = result.data.find((cat: Category) => cat.id === 1);
        if (newsParent) {
          setNewsParentCategory(newsParent);
        }
        
        // 筛选新闻中心栏目（ID为1）下的子栏目，并按sortOrder排序
        const newsCats = result.data
          .filter((cat: Category) => cat.parentId === 1 && cat.isActive)
          .sort((a: Category, b: Category) => a.sortOrder - b.sortOrder);
        
        setNewsCategories(newsCats);
        
        // 获取关于我们栏目（ID为11）的信息
        const aboutParent = result.data.find((cat: Category) => cat.id === 11);
        if (aboutParent) {
          setAboutParentCategory(aboutParent);
        }
        
        // 筛选关于我们栏目（ID为11）下的子栏目，并按sortOrder排序
        const aboutCats = result.data
          .filter((cat: Category) => cat.parentId === 11 && cat.isActive)
          .sort((a: Category, b: Category) => a.sortOrder - b.sortOrder);
        
        setAboutCategories(aboutCats);
        
        // 获取案例中心栏目（ID为33）的信息
        const caseParent = result.data.find((cat: Category) => cat.id === 33);
        if (caseParent) {
          setCaseParentCategory(caseParent);
        }
        
        // 筛选案例中心栏目（ID为33）下的子栏目，并按sortOrder排序
        const caseCats = result.data
          .filter((cat: Category) => cat.parentId === 33 && cat.isActive)
          .sort((a: Category, b: Category) => a.sortOrder - b.sortOrder);
        
        setCaseCategories(caseCats);
        
        // 获取联系我们栏目（ID为15）的信息
        const contactParent = result.data.find((cat: Category) => cat.id === 15);
        if (contactParent) {
          setContactParentCategory(contactParent);
        }
      }
      
      // 获取系统设置数据
      const settingsResponse = await fetch('/api/settings');
      const settingsResult = await settingsResponse.json();
      
      if (settingsResult.success && settingsResult.data) {
        // 将系统设置数据转换为键值对格式
        const settingsMap: any = {};
        settingsResult.data.forEach((setting: any) => {
          settingsMap[setting.key] = setting.value;
        });
        setSystemSettings(settingsMap);
        
        // 设置网站名称
        if (settingsMap.site_name) {
          setSiteName(settingsMap.site_name);
        }
      }
      
      // 获取网站Logo（从home_page_settings表）
      const homepageResponse = await fetch('/api/homepage');
      const homepageResult = await homepageResponse.json();
      
      if (homepageResult.success && homepageResult.data && homepageResult.data.siteLogo) {
        setSiteLogo(homepageResult.data.siteLogo);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    fetchApplicationCategories();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 判断导航项是否激活
  const isNavItemActive = (href: string) => {
    // 如果是关于我们页面，检查路径和哈希
    if (href.startsWith('/about/')) {
      return pathname.startsWith('/about/');
    }
    // 新闻详情页面应该高亮新闻中心导航项
    if (pathname.startsWith('/news/')) {
      return href === '/news';
    }
    // 应用详情页面应该高亮应用范围导航项
    if (pathname.startsWith('/applications/')) {
      return href === '/applications';
    }
    // 案例详情页面应该高亮案例中心导航项
    if (pathname.startsWith('/case/')) {
      return href === '/case';
    }
    // 其他页面直接比较路径
    return pathname === href;
  };

  const handleNavClick = (href: string) => {
    setActiveDropdown(null);
    
    // 处理普通链接
    router.push(href);
  };

  // 动态生成导航项
  const navItems = [
    {
      name: '网站首页',
      href: '/',
      hasDropdown: false
    },
    {
      name: applicationParentCategory?.name,
      href: '/applications',
      hasDropdown: true,
      dropdownItems: loading 
        ? [] 
        : applicationCategories.length > 0
          ? [
              // 先显示应用栏目本身
              ...applicationCategories.map(cat => ({
                name: cat.name,
                href: `/applications/${cat.slug}`
              })),
              // 再显示应用栏目的子栏目
              ...applicationCategories.flatMap(cat => 
                (cat.children || [])
                  .filter(child => child.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map(child => ({
                    name: `  ${child.name}`,
                    href: `/applications/${child.slug}`
                  }))
              )
            ]
          : []
    },
    {
      name: newsParentCategory?.name,
      href: '/news',
      hasDropdown: true,
      dropdownItems: loading 
        ? [] 
        : newsCategories.length > 0
          ? [
              // 先显示新闻栏目本身
              ...newsCategories.map(cat => ({
                name: cat.name,
                href: `/news/category/${cat.slug}`
              })),
              // 再显示新闻栏目的子栏目
              ...newsCategories.flatMap(cat => 
                (cat.children || [])
                  .filter(child => child.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map(child => ({
                    name: `  ${child.name}`,
                    href: `/news/category/${child.slug}`
                  }))
              )
            ]
          : []
    },
    {
      name: caseParentCategory?.name,
      href: '/case',
      hasDropdown: false,
    },
    {
      name: aboutParentCategory?.name,
      href: aboutCategories.length > 0 ? `/about/${aboutCategories[0].slug}` : '/about',
      hasDropdown: true,
      dropdownItems: loading 
        ? [] 
        : aboutCategories.length > 0
          ? [
              // 先显示关于我们栏目本身
              ...aboutCategories.map(cat => ({
                name: cat.name,
                href: `/about/${cat.slug}`
              })),
              // 再显示关于我们栏目的子栏目
              ...aboutCategories.flatMap(cat => 
                (cat.children || [])
                  .filter(child => child.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map(child => ({
                    name: `  ${child.name}`,
                    href: `/about/${child.slug}`
                  }))
              )
            ]
          : []
    },
    {
      name: contactParentCategory?.name,
      href: '/contact',
      hasDropdown: false
    }
  ];



  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-gray-900/95 backdrop-blur-md shadow-2xl border-b border-gray-800' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-3">
            {siteLogo ? (
              <img 
                src={siteLogo} 
                alt={siteName}
                className="w-16 h-16 object-contain"
              />
            ) : (
              <div>
              
              </div>
            )}
            <span className="text-white font-black text-2xl tracking-wide">
              {siteName}
            </span>
          </Link>

          {/* 中部导航菜单 */}
          <div className="hidden lg:flex items-center space-x-10">
            {navItems.map((item, index) => (
              <div
                key={`${item.name}-${index}`}
                className="relative"
                onMouseEnter={() => item.hasDropdown && setActiveDropdown(item.name || null)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  href={item.href}
                  className={`px-4 py-3 text-base font-bold transition-all duration-300 relative group ${
                    isNavItemActive(item.href)
                      ? 'text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.name}
                  <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-orange-500 to-red-600 transition-all duration-300 ${
                    isNavItemActive(item.href) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}></span>
                </Link>
                
                {item.hasDropdown && (
                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 mt-2 w-48 bg-gray-900/95 backdrop-blur-md rounded-lg shadow-2xl border border-gray-700 overflow-hidden"
                      >
                        {item.dropdownItems?.map((dropdownItem, index) => (
                          <Link
                            key={`${dropdownItem.name}-${index}`}
                            href={dropdownItem.href}
                            onClick={() => {
                              handleNavClick(dropdownItem.href);
                            }}
                            className="block w-full text-left px-4 py-3 text-gray-300 hover:text-white hover:bg-gray-800/50 transition-all duration-300 font-semibold"
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* 右侧行动区 */}
          <div className="flex items-center space-x-4">
            {systemSettings.header_footer_links ? (
              <Link href={systemSettings.header_footer_links} target="_blank" rel="noopener noreferrer">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-white px-6 py-2.5 rounded-full font-semibold text-base border border-gray-600 hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300"
                >
                  申请样品
                </motion.button>
              </Link>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-white px-6 py-2.5 rounded-full font-semibold text-base border border-gray-600 hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300"
              >
                申请样品
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;